import { dlog } from './debug';
/**
 * Match completion utilities for volleyball
 * Adaptive rules: Infer match format from data (best of 3 vs best of 5)
 */

export const VOLLEYBALL_RULES = {
  MAX_SETS: 5,
  SETS_1_4_POINTS: 25, // First 4 sets: first to 25 points (win by 2)
  SET_5_POINTS: 15,     // Set 5: first to 15 points (win by 2)
};

/**
 * Infer match format from sets played/won
 * @param {Array} allSetScores - Array of set scores
 * @param {Object} setsWon - { teamA: number, teamB: number }
 * @returns {string} - 'best-of-3' | 'best-of-5' | 'unknown'
 */
export function inferMatchFormat(allSetScores, setsWon) {
  const totalSetsPlayed = allSetScores.length;
  const maxSetsWon = Math.max(setsWon.teamA, setsWon.teamB);
  
  // If someone won 3 sets, definitely best of 5
  if (maxSetsWon >= 3) {
    return 'best-of-5';
  }
  
  // If someone won 2 sets and match is complete, likely best of 3
  if (maxSetsWon === 2 && totalSetsPlayed <= 3) {
    return 'best-of-3';
  }
  
  // If 4+ sets played, likely best of 5
  if (totalSetsPlayed >= 4) {
    return 'best-of-5';
  }
  
  // Special case: If only 1 set played and someone won it, treat as best-of-3
  // This handles cases where a match was ended early or only one set was needed
  if (totalSetsPlayed === 1 && maxSetsWon === 1) {
    return 'best-of-3';
  }
  
  // Default to best of 5 for high school volleyball
  return 'best-of-5';
}

/**
 * Check if a match is completed based on sets won (adaptive)
 * @param {Object} setsWon - { teamA: number, teamB: number }
 * @param {Array} allSetScores - Array of set scores (for format inference)
 * @returns {Object} - { isComplete: boolean, winner: 'teamA' | 'teamB' | null, format: string }
 */
export function checkMatchCompletion(setsWon, allSetScores = []) {
  const format = inferMatchFormat(allSetScores, setsWon);
  const setsToWin = format === 'best-of-3' ? 2 : 3;
  
  dlog('🏆 checkMatchCompletion:', {
    setsWon,
    totalSetsPlayed: allSetScores.length,
    inferredFormat: format,
    setsToWin,
    teamASetsWon: setsWon.teamA,
    teamBSetsWon: setsWon.teamB
  });
  
  if (setsWon.teamA >= setsToWin) {
    dlog('🏆 Team A wins!');
    return { isComplete: true, winner: 'teamA', format };
  }
  
  if (setsWon.teamB >= setsToWin) {
    dlog('🏆 Team B wins!');
    return { isComplete: true, winner: 'teamB', format };
  }
  
  // Special case: If only 1 set was played and someone won it, consider it complete
  // This handles cases where matches were ended early or only one set was needed
  if (allSetScores.length === 1 && (setsWon.teamA === 1 || setsWon.teamB === 1)) {
    const winner = setsWon.teamA === 1 ? 'teamA' : 'teamB';
    dlog(`🏆 Single set match complete - ${winner} wins!`);
    return { isComplete: true, winner, format: 'best-of-3' };
  }
  
  dlog('🏆 Match not complete yet');
  return { isComplete: false, winner: null, format };
}

/**
 * Check if a set is completed based on points
 * @param {number} setNumber - Current set number (1-5)
 * @param {number} teamAScore - Team A points in current set
 * @param {number} teamBScore - Team B points in current set
 * @returns {Object} - { isComplete: boolean, winner: 'teamA' | 'teamB' | null }
 */
export function checkSetCompletion(setNumber, teamAScore, teamBScore) {
  const { SETS_1_4_POINTS, SET_5_POINTS } = VOLLEYBALL_RULES;
  
  // Determine points needed to win this set
  const pointsToWin = setNumber <= 4 ? SETS_1_4_POINTS : SET_5_POINTS;
  
  // Check if either team has reached the minimum points
  if (teamAScore >= pointsToWin || teamBScore >= pointsToWin) {
    // Check win by 2 rule
    const scoreDiff = Math.abs(teamAScore - teamBScore);
    
    if (scoreDiff >= 2) {
      if (teamAScore > teamBScore) {
        return { isComplete: true, winner: 'teamA' };
      } else {
        return { isComplete: true, winner: 'teamB' };
      }
    }
  }
  
  return { isComplete: false, winner: null };
}

/**
 * Calculate sets won from all set scores
 * @param {Array} allSetScores - Array of { teamA: number, teamB: number }
 * @returns {Object} - { teamA: number, teamB: number }
 */
export function calculateSetsWon(allSetScores) {
  return allSetScores.reduce(
    (acc, set) => {
      if (set.teamA > set.teamB) {
        acc.teamA += 1;
      } else if (set.teamB > set.teamA) {
        acc.teamB += 1;
      }
      return acc;
    },
    { teamA: 0, teamB: 0 }
  );
}

/**
 * Get match status based on completion (adaptive)
 * @param {Object} setsWon - { teamA: number, teamB: number }
 * @param {Array} allSetScores - Array of set scores (for format inference)
 * @param {boolean} isLive - Whether match is currently being played
 * @returns {string} - 'LIVE' | 'FINAL'
 */
export function getMatchStatus(setsWon, allSetScores = [], isLive = true) {
  const { isComplete } = checkMatchCompletion(setsWon, allSetScores);
  return isComplete ? 'FINAL' : (isLive ? 'LIVE' : 'FINAL');
}
